import { Check, Flag, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { DeltaCell } from "@/components/cinch/DeltaCell";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  useReviewBilledAccount,
  type BilledAccount,
  type ReviewStatus,
} from "../api";
import { sourcesMismatch } from "../billedActuals";
import { fmtCur2 } from "../../renewals/format";
import { BilledLinesTable } from "./BilledLinesTable";

/** Review quick-view for one billed-actuals account row: the numbers, the
 *  invoice lines, and the verdict (approve / flag + notes). Opens from a row
 *  click or the row's flag button; URL state (`?account=&expanded=`) is owned
 *  by the parent panel. */
export function BilledAccountDrawer({
  period,
  account,
  canAct,
  expanded,
  presetFlag,
  onExpandedChange,
  onClose,
}: {
  period: string;
  account: BilledAccount | null;
  canAct: boolean;
  expanded: boolean;
  /** True when the drawer was opened via a row's Flag button — preselects the
   *  flagged verdict so the reviewer lands straight in the notes box. */
  presetFlag: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}) {
  const review = useReviewBilledAccount(period);
  const [status, setStatus] = useState<ReviewStatus>("pending");
  const [notes, setNotes] = useState("");
  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  // Seed the form when the target account changes (not on every refetch).
  const seededKey = useRef<number | null>(null);
  useEffect(() => {
    if (account && account.qb_id !== seededKey.current) {
      seededKey.current = account.qb_id;
      setStatus(presetFlag ? "flagged" : account.review_status);
      setNotes(account.review_notes ?? "");
      if (presetFlag) setTimeout(() => notesRef.current?.focus(), 150);
    }
    if (!account) seededKey.current = null;
  }, [account, presetFlag]);

  const open = account != null;
  const delta = account ? account.billed_current - account.billed_prior : 0;
  const dirty =
    !!account &&
    (status !== account.review_status || (notes ?? "") !== (account.review_notes ?? ""));

  function save() {
    if (!account) return;
    review.mutate(
      { qbId: account.qb_id, review: { status, notes } },
      {
        onSuccess: (updated) =>
          toast.success(
            updated.review_status === "flagged"
              ? `${title(updated)} flagged`
              : updated.review_status === "approved"
                ? `${title(updated)} approved`
                : `${title(updated)} reset to pending`,
          ),
        onError: (e) =>
          toast.error("Couldn't save review", { description: (e as Error).message }),
      },
    );
  }

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      title={account ? title(account) : ""}
      subtitle={
        account
          ? `QB #${account.qb_id} · ${monthLabel(period)} billed actuals`
          : undefined
      }
      onClose={onClose}
    >
      {account && (
        <div className="flex flex-col gap-5 p-4">
          {/* The numbers under review */}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border px-4 py-3 sm:grid-cols-3">
            <Stat label={`Billed ${monthLabel(prevMonth(period))}`}>
              {fmtCur2(account.billed_prior)}
            </Stat>
            <Stat label={`Billed ${monthLabel(period)}`}>
              <span className="font-semibold">{fmtCur2(account.billed_current)}</span>
              <DeltaCell value={delta} className="ml-2 text-xs" />
            </Stat>
            <Stat label="MRR this month">{fmtCur2(account.mrr_current)}</Stat>
            <Stat label="QB invoices (net)">
              {account.qb_invoice_total != null ? fmtCur2(account.qb_invoice_total) : "—"}
              {account.qb_doc_count != null && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({account.qb_doc_count} doc{account.qb_doc_count === 1 ? "" : "s"})
                </span>
              )}
            </Stat>
            <Stat label="Waterfall vs QB">
              {sourcesMismatch(account) ? (
                <span className="font-semibold text-warning">
                  {fmtCur2((account.qb_invoice_total ?? 0) - account.billed_current)} off
                </span>
              ) : (
                <span className="text-success">In sync</span>
              )}
            </Stat>
            <Stat label="QB customer">{account.qb_customer_name ?? "—"}</Stat>
          </dl>

          {/* Verdict */}
          <div className="space-y-3 rounded-md border px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Review
              </span>
              <StatusBadge value={account.review_status} />
            </div>
            <div className="flex flex-wrap gap-2">
              <VerdictButton
                active={status === "approved"}
                activeClass="border-success bg-success-light text-success"
                onClick={() => setStatus("approved")}
                disabled={!canAct}
              >
                <Check className="h-3.5 w-3.5" /> Approve
              </VerdictButton>
              <VerdictButton
                active={status === "flagged"}
                activeClass="border-warning bg-warning-light text-warning"
                onClick={() => setStatus("flagged")}
                disabled={!canAct}
              >
                <Flag className="h-3.5 w-3.5" /> Flag
              </VerdictButton>
              <VerdictButton
                active={status === "pending"}
                activeClass="border-border bg-muted text-foreground"
                onClick={() => setStatus("pending")}
                disabled={!canAct}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Clear
              </VerdictButton>
            </div>
            <Textarea
              ref={notesRef}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canAct}
              rows={3}
              aria-label="Review notes"
              placeholder={
                status === "flagged"
                  ? "What looks off? (required context for whoever clears the flag)"
                  : "Optional notes…"
              }
            />
            {account.reviewed_at && (
              <p className="text-xs text-muted-foreground">
                Last reviewed by {account.reviewed_by_name ?? "—"} on{" "}
                {new Date(account.reviewed_at).toLocaleString()}.
              </p>
            )}
            <div className="flex items-center gap-3">
              <Button onClick={save} disabled={!canAct || !dirty || review.isPending}>
                {review.isPending ? "Saving…" : "Save review"}
              </Button>
              {!canAct && (
                <span className="text-xs text-muted-foreground">
                  Only an R-01 owner (RevOps) can record a verdict.
                </span>
              )}
            </div>
          </div>

          {/* As billed on the invoices */}
          <div className="space-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Invoice lines · {monthLabel(period)}
            </span>
            <div className="overflow-hidden rounded-md border">
              <BilledLinesTable lines={account.lines} compact={!expanded} />
            </div>
          </div>
        </div>
      )}
    </EntityDrawer>
  );
}

function title(a: BilledAccount): string {
  return a.cinch_account_name ?? a.qb_customer_name ?? `QB #${a.qb_id}`;
}

function prevMonth(ym: string): string {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm tabular-nums">{children}</dd>
    </div>
  );
}

function VerdictButton({
  active,
  activeClass,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  activeClass: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50",
        active ? activeClass : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
